import { opaqueHex, paintToCss, parseHex, withAlpha } from "./color";
import {
  type Shadow,
  type TextEffect,
  type TextEffectType,
  type TextElement,
  textEffectSchema,
} from "./schema";

/**
 * Text effects and curved text (docs/04-dokumentformat.md): pure mappings
 * from the element to CSS / SVG values, shared by the renderer (all outputs)
 * and the editor. Every size scales with the element's font size.
 */

/** Style values keyed like React's CSSProperties (camelCase). */
export type EffectCss = Record<string, string | number>;

/** Everything the effects panel offers; shadow and outline are the element's own fields. */
export type TextEffectChoice = "none" | "shadow" | "outline" | TextEffectType;

export const TEXT_EFFECT_CHOICES: { value: TextEffectChoice; label: string }[] = [
  { value: "none", label: "Keine" },
  { value: "shadow", label: "Schatten" },
  { value: "lift", label: "Anheben" },
  { value: "hollow", label: "Hohl" },
  { value: "outline", label: "Kontur" },
  { value: "neon", label: "Neon" },
  { value: "echo", label: "Echo" },
  { value: "glitch", label: "Versatz" },
  { value: "highlight", label: "Hintergrund" },
  { value: "gradient", label: "Verlauf" },
];

export function defaultTextEffect(type: TextEffectType): TextEffect {
  if (type === "gradient") {
    return {
      type,
      fill: {
        type: "linear",
        angle: 90,
        stops: [
          { offset: 0, color: "#f97316" },
          { offset: 1, color: "#db2777" },
        ],
      },
    };
  }
  return textEffectSchema.parse({ type });
}

/** Which tile of the panel is active for this element. */
export function activeTextEffect(element: TextElement): TextEffectChoice {
  if (element.effect) return element.effect.type;
  if (element.shadow) return "shadow";
  if (element.style.stroke && element.style.stroke.width > 0) return "outline";
  return "none";
}

const round = (value: number) => Math.round(value * 100) / 100;

/** Unit vector of a direction in degrees (0 = right, 90 = down). */
function direction(degrees: number): { x: number; y: number } {
  const rad = (degrees * Math.PI) / 180;
  return { x: Math.cos(rad), y: Math.sin(rad) };
}

export function shadowCss(shadow: Shadow): string {
  return `${shadow.x}px ${shadow.y}px ${shadow.blur}px ${shadow.color}`;
}

/** `text-shadow` layers of an effect (lift, neon, echo, glitch). */
export function effectTextShadows(effect: TextEffect, color: string, fontSize: number): string[] {
  const fs = fontSize;
  switch (effect.type) {
    case "lift": {
      const k = effect.intensity / 100;
      return [
        `0px ${round(fs * (0.02 + 0.05 * k))}px ${round(fs * (0.1 + 0.35 * k))}px ${withAlpha("#000000", 0.12 + 0.43 * k)}`,
      ];
    }
    case "neon": {
      const k = 0.3 + effect.intensity / 100;
      const glow = opaqueHex(color);
      return [0.04, 0.12, 0.3].map((size) => `0px 0px ${round(fs * size * k)}px ${glow}`);
    }
    case "echo": {
      const step = (effect.offset / 100) * 0.2 * fs;
      const dir = direction(effect.direction);
      const base = effect.color ?? color;
      const alpha = parseHex(base).a;
      return [1, 2].map(
        (i) =>
          `${round(dir.x * step * i)}px ${round(dir.y * step * i)}px 0px ${withAlpha(base, (alpha * 0.55) / i)}`,
      );
    }
    case "glitch": {
      const step = (effect.offset / 100) * 0.1 * fs;
      const dir = direction(effect.direction);
      return [
        `${round(-dir.x * step)}px ${round(-dir.y * step)}px 0px ${effect.colors[0]}`,
        `${round(dir.x * step)}px ${round(dir.y * step)}px 0px ${effect.colors[1]}`,
      ];
    }
    default:
      return [];
  }
}

/** Stroke width of hollow text (centred on the glyph outline). */
export function hollowWidth(thickness: number, fontSize: number): number {
  return round(Math.max(0.5, fontSize * (0.01 + (thickness / 100) * 0.07)));
}

/**
 * CSS for the text content box. Empty without an effect, so documents
 * without effects render exactly as before.
 */
export function textEffectCss(element: TextElement): EffectCss {
  const effect = element.effect;
  if (!effect) return {};
  const { style } = element;
  const out: EffectCss = {};
  const shadows = effectTextShadows(effect, style.color, style.fontSize);
  if (element.shadow) shadows.push(shadowCss(element.shadow));
  if (shadows.length > 0) out.textShadow = shadows.join(", ");
  if (effect.type === "hollow") {
    out.WebkitTextFillColor = "transparent";
    out.WebkitTextStroke = `${hollowWidth(effect.thickness, style.fontSize)}px ${style.color}`;
    out.paintOrder = "normal";
  } else if (effect.type === "gradient") {
    out.backgroundImage = paintToCss(effect.fill);
    out.WebkitBackgroundClip = "text";
    out.backgroundClip = "text";
    out.WebkitTextFillColor = "transparent";
  }
  return out;
}

/**
 * Highlight box behind every line: an inline wrapper whose padding is a
 * spread box-shadow, so it does not change the line breaks.
 */
export function highlightCss(element: TextElement): EffectCss | null {
  const effect = element.effect;
  if (effect?.type !== "highlight") return null;
  const fs = element.style.fontSize;
  const color = withAlpha(effect.color, parseHex(effect.color).a * effect.opacity);
  const padding = round((effect.padding / 100) * 0.4 * fs);
  return {
    backgroundColor: color,
    boxShadow: `0px 0px 0px ${padding}px ${color}`,
    borderRadius: `${round((effect.radius / 100) * 0.4 * fs)}px`,
    WebkitBoxDecorationBreak: "clone",
    boxDecorationBreak: "clone",
  };
}

/** The effect needs transparency in a PDF (soft glows, fading copies, see-through colours). */
export function textEffectTransparent(element: TextElement): boolean {
  const effect = element.effect;
  if (!effect) return false;
  switch (effect.type) {
    case "lift":
    case "neon":
    case "echo":
      return true;
    case "glitch":
      return effect.colors.some((color) => parseHex(color).a < 0.999);
    case "highlight":
      return effect.opacity < 0.999 || parseHex(effect.color).a < 0.999;
    case "gradient":
      return effect.fill.stops.some((stop) => parseHex(stop.color).a < 0.999);
    default:
      return false;
  }
}

// ---------------------------------------------------------------------------
// Curved text
// ---------------------------------------------------------------------------

/** Share of the font size above / below the baseline taken into the box. */
export const CURVE_ASCENT = 0.8;
export const CURVE_DESCENT = 0.25;

export interface CurveGeometry {
  /** Radius of the baseline circle. */
  radius: number;
  /** Angle the text covers, radians. */
  sweep: number;
  /** Size of the box around the curved text. */
  width: number;
  height: number;
  /** Circle centre inside that box. */
  cx: number;
  cy: number;
  /** Circle for SVG `textPath`, starting opposite the text so 50 % is its middle. */
  path: string;
}

/** A curve of 0 (or none) is straight text. */
export function isCurved(element: Pick<TextElement, "curve">): boolean {
  return element.curve !== undefined && Math.abs(element.curve) >= 1;
}

/**
 * Geometry of text of the given straight length bent along a circle: at
 * ±100 % it closes into a full circle. Positive arches up (glyphs outside the
 * circle), negative arches down (glyphs inside).
 */
export function curveGeometry(length: number, curve: number, fontSize: number): CurveGeometry {
  const amount = Math.max(1, Math.min(100, Math.abs(curve))) / 100;
  const sweep = amount * 2 * Math.PI;
  const radius = Math.max(1, length) / sweep;
  const up = curve > 0;
  const ascent = fontSize * CURVE_ASCENT;
  const descent = fontSize * CURVE_DESCENT;
  const outer = radius + (up ? ascent : descent);
  const inner = Math.max(0, radius - (up ? descent : ascent));
  // Text centre at the top (up) or bottom (down); φ measured from there.
  const sign = up ? -1 : 1;
  const angles: number[] = [];
  const steps = 64;
  for (let i = 0; i <= steps; i++) angles.push(-sweep / 2 + (sweep * i) / steps);
  for (const extreme of [-Math.PI, -Math.PI / 2, Math.PI / 2, Math.PI]) {
    if (Math.abs(extreme) <= sweep / 2) angles.push(extreme);
  }
  let minX = Number.POSITIVE_INFINITY;
  let maxX = Number.NEGATIVE_INFINITY;
  let minY = Number.POSITIVE_INFINITY;
  let maxY = Number.NEGATIVE_INFINITY;
  for (const phi of angles) {
    for (const r of [inner, outer]) {
      const x = r * Math.sin(phi);
      const y = sign * r * Math.cos(phi);
      minX = Math.min(minX, x);
      maxX = Math.max(maxX, x);
      minY = Math.min(minY, y);
      maxY = Math.max(maxY, y);
    }
  }
  const cx = -minX;
  const cy = -minY;
  const r = round(radius);
  const start = up ? cy + radius : cy - radius;
  const mid = up ? cy - radius : cy + radius;
  const flag = up ? 1 : 0;
  const path =
    `M ${round(cx)} ${round(start)} A ${r} ${r} 0 1 ${flag} ${round(cx)} ${round(mid)} ` +
    `A ${r} ${r} 0 1 ${flag} ${round(cx)} ${round(start)}`;
  return {
    radius,
    sweep,
    width: maxX - minX,
    height: maxY - minY,
    cx,
    cy,
    path,
  };
}

/** Rough straight length of a line of text before the browser has measured it. */
export function estimateTextLength(text: string, fontSize: number, letterSpacing = 0): number {
  return [...text].length * fontSize * (0.56 + letterSpacing);
}

/** Curved text is one line: line breaks become spaces. */
export function curvedLine(text: string): string {
  return text.replace(/\n+/g, " ");
}

/** SVG paint of curved text (fill, outline, hollow); the gradient id comes from the renderer. */
export function curveTextPaint(
  element: TextElement,
  gradientId?: string,
): { fill: string; stroke?: string; strokeWidth?: number; paintOrder?: string } {
  const { style, effect } = element;
  if (effect?.type === "hollow") {
    return {
      fill: "none",
      stroke: style.color,
      strokeWidth: hollowWidth(effect.thickness, style.fontSize),
    };
  }
  const fill = effect?.type === "gradient" && gradientId ? `url(#${gradientId})` : style.color;
  if (style.stroke && style.stroke.width > 0) {
    return {
      fill,
      stroke: style.stroke.color,
      strokeWidth: style.stroke.width * 2,
      paintOrder: "stroke fill",
    };
  }
  return { fill };
}

/** `text-shadow` of curved text (effect layers plus the element's shadow). */
export function curveTextShadow(element: TextElement): string | undefined {
  const shadows = element.effect
    ? effectTextShadows(element.effect, element.style.color, element.style.fontSize)
    : [];
  if (element.shadow) shadows.push(shadowCss(element.shadow));
  return shadows.length > 0 ? shadows.join(", ") : undefined;
}
